import React, { useState, useEffect, useRef } from "react";
import { quotes as quotesApi } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Check } from "lucide-react";
import { IDS } from "@/constants/testIds";

/**
 * Renders a photo tile with editable AI caption.
 */
export default function PhotoTile({ quoteId, photo, disabled, onRemove, onCaptionChange }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(photo.caption || "");
  const [busy, setBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setText(photo.caption || ""); }, [photo.caption]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    const val = text.trim();
    setBusy(true);
    try {
      const { data } = await quotesApi.updatePhotoCaption(quoteId, photo.id, val);
      onCaptionChange?.(photo.id, data.caption);
      setEditing(false);
    } catch (e) {
      toast.error("Could not save caption");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setRegenBusy(true);
    try {
      const { data } = await quotesApi.regeneratePhotoCaption(quoteId, photo.id);
      setText(data.caption);
      onCaptionChange?.(photo.id, data.caption);
      toast.success("Caption refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI caption unavailable");
    } finally { setRegenBusy(false); }
  };

  return (
    <div
      data-testid={IDS.editor.photoItem(photo.id)}
      className="group relative rounded-md overflow-hidden border border-slate-200 bg-slate-50"
    >
      <div className="aspect-square relative">
        <img
          src={quotesApi.photoUrl(quoteId, photo.id)}
          alt={photo.filename}
          className="w-full h-full object-cover"
        />
        {!disabled && (
          <button
            type="button"
            data-testid={IDS.editor.photoRemove(photo.id)}
            onClick={() => onRemove?.(photo.id)}
            className="absolute top-1.5 right-1.5 h-8 w-8 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-[#EF4444] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Remove photo"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <div className="px-2.5 py-2 bg-white border-t border-slate-200">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type="text"
              data-testid={IDS.editor.photoCaptionInput(photo.id)}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              placeholder="Caption…"
              className="flex-1 text-xs border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-[#2563EB]"
              maxLength={120}
              disabled={busy}
            />
            <button
              type="button"
              data-testid={IDS.editor.photoCaptionSave(photo.id)}
              onClick={save}
              disabled={busy}
              className="h-7 w-7 rounded flex items-center justify-center text-[#2563EB] hover:bg-blue-50"
              aria-label="Save caption"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <div
              data-testid={IDS.editor.photoCaption(photo.id)}
              onClick={() => !disabled && setEditing(true)}
              className={`flex-1 text-[11px] leading-snug min-h-[16px] ${
                text ? "text-[#334155]" : "text-[#94A3B8] italic"
              } ${disabled ? "" : "cursor-text hover:text-[#2563EB]"}`}
              title={disabled ? "" : "Click to edit"}
            >
              {text || "Add a caption…"}
            </div>
            {!disabled && (
              <button
                type="button"
                data-testid={IDS.editor.photoCaptionRegen(photo.id)}
                onClick={regenerate}
                disabled={regenBusy}
                className="h-6 w-6 rounded flex items-center justify-center text-[#8B5CF6] hover:bg-purple-50"
                title="Regenerate AI caption"
                aria-label="Regenerate AI caption"
              >
                {regenBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
